import { Injectable, isDevMode } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { delay } from 'rxjs/operators';

const LOADER_TIMEOUT_MS = 30000; // 30 seconds safety timeout

@Injectable({
  providedIn: 'root'
})
export class LoaderService {
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private loadingCountSubject = new BehaviorSubject<number>(0);
  private timeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor() {}

  /**
   * Observable pour suivre l'état de chargement global
   */
  get isLoading$(): Observable<boolean> {
    return this.loadingSubject.asObservable().pipe(delay(0));
  }

  /**
   * Démarre le chargement
   */
  startLoading(): void {
    const currentCount = this.loadingCountSubject.value;
    this.loadingCountSubject.next(currentCount + 1);
    this.loadingSubject.next(true);
    this.resetTimeout();
  }

  /**
   * Arrête le chargement
   */
  stopLoading(): void {
    const currentCount = this.loadingCountSubject.value;
    const newCount = Math.max(0, currentCount - 1);
    this.loadingCountSubject.next(newCount);

    if (newCount === 0) {
      this.loadingSubject.next(false);
      this.clearTimeout();
    }
  }

  /**
   * Force l'arrêt de tous les chargements
   */
  forceStopLoading(): void {
    this.loadingCountSubject.next(0);
    this.loadingSubject.next(false);
    this.clearTimeout();
  }

  private resetTimeout(): void {
    this.clearTimeout();
    this.timeoutId = setTimeout(() => {
      if (this.loadingSubject.value) {
        if (isDevMode()) {
          console.warn('[LoaderService] Loading timeout reached, forcing stop');
        }
        this.forceStopLoading();
      }
    }, LOADER_TIMEOUT_MS);
  }

  private clearTimeout(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  /**
   * Retourne l'état actuel du chargement
   */
  get isLoading(): boolean {
    return this.loadingSubject.value;
  }
}
